import { useColorMode } from '@chakra-ui/react';
import { useLayoutEffect, useSyncExternalStore } from 'react';
import {
  getServerColorModePreference,
  readStoredColorModePreference,
  subscribeToColorModePreference,
} from '../hooks/ui/colorModePreferenceStore';

/**
 * Makes Chakra's resolved colour mode follow the user's three-state
 * preference: an explicit "Light" / "Dark" is asserted as-is, and "System"
 * (the default when nothing is stored) follows the OS scheme for as long as
 * the app is open.
 *
 * This component is the ONLY owner of OS-following. `theme/index.ts` sets
 * `useSystemColorMode: false` because Chakra's own listener
 * (`color-mode-provider.cjs`) is armed unconditionally when that flag is on
 * and knows nothing about the preference: a user who chose "Light" was still
 * flipped when the OS went dark, and Chakra persisted the flip to
 * `chakra-ui-color-mode`, so the choice was silently lost.
 *
 * An explicit preference is re-asserted on mount rather than trusted to
 * Chakra's restore, because Chakra restores from `chakra-ui-color-mode`, and
 * that key may hold a stale value left by exactly that flip (or by anything
 * else that writes it). `bower-color-mode-preference` is the source of
 * truth; Chakra's key is derived from it.
 *
 * Applied in a layout effect so the correction is written before React yields
 * to the browser, instead of depending on the passive-effect flush winning
 * that race. Chakra's provider renders `initialColorMode` ('light') on the
 * first pass whatever is stored — `getTheme` in `color-mode-provider.cjs`
 * returns the fallback for every manager except the SSR cookie one — and
 * reads `chakra-ui-color-mode` only in a passive effect afterwards, so the
 * first committed tree is light until something corrects it.
 *
 * Honest limit on that: measured against the local dev server (a
 * `PerformanceObserver` on first-contentful-paint versus a `MutationObserver`
 * on `data-theme`), BOTH orderings landed the dark markers before FCP — 267ms
 * vs 292ms passive, 250ms vs 272ms layout. So no visible flash was
 * demonstrated either way here, and this is a correctness-by-construction
 * choice rather than a fix for a flash anyone has seen. Do not weaken it back
 * to a passive effect on the strength of that measurement: it says the race
 * was won on one machine, not that there is no race.
 *
 * Chakra's passive effect then re-reads the key this already wrote, so the
 * two agree.
 *
 * When this logic lived inside `useColorModePreference`, whose only consumer
 * is the Settings page, it ran on every visit to Settings (flipping the theme
 * over what Chakra had restored) and stopped following the OS the moment
 * Settings unmounted.
 *
 * Reads the preference from the shared store, so a choice made in the
 * Preferences tab takes effect here immediately.
 *
 * Renders nothing. Mount once, inside `ChakraProvider`.
 */
export function ColorModePreferenceSync(): null {
  const { setColorMode } = useColorMode();

  const preference = useSyncExternalStore(
    subscribeToColorModePreference,
    readStoredColorModePreference,
    getServerColorModePreference,
  );

  useLayoutEffect(() => {
    if (preference !== 'system') {
      setColorMode(preference);
      return;
    }

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setColorMode(mq.matches ? 'dark' : 'light');
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [preference, setColorMode]);

  return null;
}
